// Copyright (c) StrangeBee 2023

import { getAntdSelectOptions, useLocalMessage } from '@Helpers/';
import { useListAllFields } from '@Queries/describeQueries';
import { FormInstance } from 'antd/lib/form';
import { Store } from 'antd/lib/form/interface';
import React, { ReactElement } from 'react';
import { ObjectTypeV1 } from 'thehive-sdk';
import { AllField } from '@Organisms/Dashboard/WidgetManage/AllField';
import { THForm } from '@Molecules/THForm/THForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THColorPicker } from '@Molecules/Inputs/THColorPicker/THColorPicker';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';

import './SerieForm.scss';

interface SerieFormProps {
    textField?: boolean;
    lineField?: boolean;
    index: number;
    form: FormInstance;
    entity: string;
    counterField?: boolean;
    entitiesToExclude?: string[];
}

const AggLabels = ['count', 'sum', 'avg', 'min', 'max'];

const SerieForm = ({
    textField,
    index,
    form,
    entity,
    lineField,
    counterField,
    entitiesToExclude,
}: SerieFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const { data: describeData } = useListAllFields();

    if (!describeData) {
        return null;
    }

    const shouldUpdate = (prevValues: Store, nextValues: Store): boolean => {
        return (
            prevValues['series'][index]?.entity !== nextValues['series'][index]?.entity ||
            prevValues['entity'] !== nextValues['entity']
        );
    };

    return (
        <div className="serie-form">
            <div className="serie-form__section">
                {(textField || lineField) && (
                    <THForm.Item
                        name={[index, 'entity']}
                        label={i('app.dashboard.widget.create.form.entity.label')}
                        rules={[
                            {
                                required: true,
                                message: i('common.is-required'),
                            },
                        ]}
                    >
                        <THSelect
                            placeholder={i('app.dashboard.widget.create.form.entity.placeholder')}
                            options={getAntdSelectOptions(
                                Object.keys(describeData).filter((entity) => !entitiesToExclude?.includes(entity)),
                            )}
                        />
                    </THForm.Item>
                )}

                {lineField && (
                    <THForm.Item noStyle shouldUpdate={shouldUpdate}>
                        {(): ReactElement | null =>
                            describeData ? (
                                <AllField
                                    describeData={describeData}
                                    fieldType={['date']}
                                    formItemName={[index, 'dateField']}
                                    fieldName="dateField"
                                    entity={form.getFieldValue(['series', index, 'entity'])}
                                    form={form}
                                    aggregationWarning={false}
                                />
                            ) : null
                        }
                    </THForm.Item>
                )}

                <THForm.Item noStyle shouldUpdate={shouldUpdate}>
                    {(): ReactElement | null =>
                        describeData ? (
                            <AllField
                                describeData={describeData}
                                fieldType={['date']}
                                formItemName={[index, 'periodDateField']}
                                fieldName="periodDateField"
                                entity={form.getFieldValue(['series', index, 'entity'])}
                                form={form}
                                aggregationWarning={false}
                            />
                        ) : null
                    }
                </THForm.Item>
            </div>

            <div className="serie-form__section">
                <THForm.Item
                    name={[index, 'agg']}
                    label={i('app.dashboard.widget.create.form.series.agg.label')}
                    rules={[
                        {
                            required: true,
                            message: i('common.is-required'),
                        },
                    ]}
                >
                    <THSelect
                        placeholder={i('app.dashboard.widget.create.form.series.agg.placeholder')}
                        options={getAntdSelectOptions(AggLabels)}
                    />
                </THForm.Item>

                <THForm.Item<any>
                    noStyle
                    shouldUpdate={(prevValues, currValues): boolean =>
                        prevValues['series'][index] !== currValues['series'][index]
                    }
                >
                    {() => {
                        const aggValue = form.getFieldValue('series')[index]?.agg;

                        if (!aggValue || aggValue === 'count' || !entity) {
                            return null;
                        }

                        return (
                            <AllField
                                describeData={describeData}
                                fieldType={['number', 'integer', 'float']}
                                formItemName={[index, 'field']}
                                fieldName="field"
                                entity={entity as ObjectTypeV1}
                                form={form}
                                aggregationWarning={false}
                            />
                        );
                    }}
                </THForm.Item>
            </div>

            <div className="serie-form__section">
                <THForm.Item name={[index, 'label']} label={i('app.dashboard.widget.create.form.series.label.label')}>
                    <THInput placeholder={i('app.dashboard.widget.create.form.series.label.placeholder')} />
                </THForm.Item>

                {!textField && !counterField && (
                    <THForm.Item noStyle shouldUpdate={shouldUpdate}>
                        {(): ReactElement => (
                            <THForm.Item label={i('dashboard.serie.type.label')} name={[index, 'type']}>
                                <THSelect
                                    options={['line', 'area', 'spline', 'area-spline', 'bar'].map((value) => ({
                                        value,
                                        label: i(`dashboard.serie.type.${value}`),
                                    }))}
                                />
                            </THForm.Item>
                        )}
                    </THForm.Item>
                )}

                {!textField && !counterField && (
                    <THForm.Item noStyle shouldUpdate={shouldUpdate}>
                        {(): ReactElement => (
                            <THForm.Item label={i('general.color')} name={[index, 'color']}>
                                <THColorPicker />
                            </THForm.Item>
                        )}
                    </THForm.Item>
                )}
            </div>

            {counterField && (
                <div className="serie-form__section">
                    <THForm.Item name={[index, 'threshold']} label="Threshold">
                        <THInputNumber />
                    </THForm.Item>

                    <THForm.Item name={[index, 'orientation']} label="Orientation">
                        <THRadio.Group>
                            <THRadio.Button value="min">{i('general.minimum')}</THRadio.Button>
                            <THRadio.Button value="max">{i('general.maximum')}</THRadio.Button>
                        </THRadio.Group>
                    </THForm.Item>
                </div>
            )}
        </div>
    );
};

export default SerieForm;
